import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    OutlinedInput,
    Paper,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useConfiguracoesPedidoContext } from "../../../pedidos/contexts/ConfiguracoesPedidoContext";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { TipoPedido, TipoPedidoTitulo } from "../../../pedidos/enum/TipoPedido";
import { ITaxaServico } from "../../../pedidos/interface/ITaxaServico";

const CONTEXTOS_DISPONIVEIS = [TipoPedido.simples, TipoPedido.mesa];

export const TaxaServico = () => {
    const { configuracao, salvarTaxaServico, removerTaxaServico } = useConfiguracoesPedidoContext();
    const snackbar = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const [percentual, setPercentual] = useState<string>("");
    const [contextos, setContextos] = useState<TipoPedido[]>([]);

    useEffect(() => {
        const config = configuracao?.taxaServico;
        if (config) {
            setPercentual(config.percentual?.toString() ?? "");
            setContextos(config.contextos ?? []);
        }
    }, [configuracao]);

    const alternarContexto = (contexto: TipoPedido) => {
        setContextos(atual =>
            atual.includes(contexto)
                ? atual.filter(c => c !== contexto)
                : [...atual, contexto]
        );
    };

    const handleSalvar = async () => {
        const valor = Number(percentual);

        if (isNaN(valor) || valor < 0 || valor > 100) {
            return snackbar("Informe um percentual entre 0 e 100", { severity: "error" });
        }

        if (!contextos.length) {
            return snackbar("Selecione ao menos um contexto", { severity: "error" });
        }

        const config: ITaxaServico = { percentual: valor, contextos };

        setLoading(true);
        await salvarTaxaServico(config);
        setLoading(false);
    };

    const handleRemover = async () => {
        setConfirmDialogOpen(false);
        setLoading(true);
        await removerTaxaServico();
        setPercentual("");
        setContextos([]);
        setLoading(false);
    };

    return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", p: "1rem" }}>
            <Box sx={{ width: "100%", maxWidth: "700px" }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: "1rem" }}>
                    Taxa de serviço
                </Typography>
                <Typography variant="subtitle2" color="GrayText" sx={{ mb: "1.5rem" }}>
                    Configure um percentual de taxa de serviço a ser aplicado automaticamente nos pedidos do PDV.
                    Escolha em quais contextos a taxa será sugerida. O operador poderá remover ou editar o valor em cada pedido.
                </Typography>

                <Paper sx={{ p: "1.5rem", mb: "1rem" }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: "1rem" }}>
                        Percentual
                    </Typography>
                    <OutlinedInput
                        type="number"
                        value={percentual}
                        onChange={e => setPercentual(e.target.value)}
                        endAdornment={<InputAdornment position="end">%</InputAdornment>}
                        inputProps={{ min: 0, max: 100, step: "0.01" }}
                        fullWidth
                    />
                </Paper>

                <Paper sx={{ p: "1.5rem", mb: "1rem" }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: "0.5rem" }}>
                        Aplicar nos contextos
                    </Typography>
                    <FormGroup>
                        {CONTEXTOS_DISPONIVEIS.map(contexto => (
                            <FormControlLabel
                                key={contexto}
                                control={
                                    <Checkbox
                                        checked={contextos.includes(contexto)}
                                        onChange={() => alternarContexto(contexto)}
                                    />
                                }
                                label={TipoPedidoTitulo[contexto]}
                            />
                        ))}
                    </FormGroup>
                </Paper>

                <Box sx={{ display: "flex", gap: "1rem", justifyContent: "space-between" }}>
                    {configuracao?.taxaServico && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setConfirmDialogOpen(true)}
                            disabled={loading}
                        >
                            Remover configuração
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleSalvar}
                        disabled={loading}
                        sx={{ ml: "auto" }}
                    >
                        {loading ? <CircularProgress size={24} /> : "Salvar"}
                    </Button>
                </Box>
            </Box>

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Remover taxa de serviço</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja remover a configuração de taxa de serviço?
                        Os pedidos futuros não terão a taxa sugerida automaticamente.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
                    <Button color="error" onClick={handleRemover}>
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
